import { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { confirmDialog } from '../../lib/confirmDialog';

const IMAGE_BUCKET = 'portal-assets';

export default function GallerySectionBuilder({
  teamId,
  sectionId,
  items = [],
  onItemsUpdate
}) {
  const [localItems, setLocalItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);
  const [newItem, setNewItem] = useState({
    title: '',
    caption: '',
    file: null
  });

  useEffect(() => {
    const filtered = items.filter((item) => item.section_id === sectionId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    setLocalItems(filtered);
  }, [items, sectionId]);

  const canMutate = Boolean(teamId && sectionId);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setNewItem((prev) => ({ ...prev, file }));
  };

  const uploadImage = async (file) => {
    const fileExt = file.name.split('.').pop() || 'png';
    const storagePath = `${teamId}/gallery/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from(IMAGE_BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage.from(IMAGE_BUCKET).getPublicUrl(storagePath);
    if (!data?.publicUrl) {
      throw new Error('Unable to generate public URL for uploaded image.');
    }

    return { publicUrl: data.publicUrl, storagePath };
  };

  const handleAddItem = async () => {
    if (!canMutate) {
      toast.error('Please save the section before adding gallery items.');
      return;
    }

    if (!newItem.file) {
      toast.error('Please select an image file.');
      return;
    }

    setSaving(true);

    try {
      const { publicUrl, storagePath } = await uploadImage(newItem.file);

      const insertPayload = {
        team_id: teamId,
        section_id: sectionId,
        title: newItem.title.trim() || null,
        caption: newItem.caption.trim() || null,
        image_url: publicUrl,
        storage_path: storagePath,
        sort_order: localItems.length,
        visible: true
      };

      const { data, error } = await supabase
        .from('portal_gallery_items')
        .insert(insertPayload)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const updated = [...localItems, data];
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      setNewItem({ title: '', caption: '', file: null });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setIsAdding(false);
      toast.success('Image added to gallery');
    } catch (error) {
      console.error('Error adding gallery item:', error);
      const message = error?.message?.includes('Storage bucket not found')
        ? 'Portal assets bucket not found. Please create a "portal-assets" bucket in Supabase Storage and mark it public.'
        : error.message || 'Failed to add gallery item.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    setSaving(true);
    const payload = { ...updates };

    try {
      const { error } = await supabase
        .from('portal_gallery_items')
        .update(payload)
        .eq('id', itemId);

      if (error) {
        throw error;
      }

      const nextItems = localItems.map((item) => (item.id === itemId ? { ...item, ...payload } : item));
      setLocalItems(nextItems);
      onItemsUpdate?.(sectionId, nextItems);
      setEditingId(null);
      toast.success('Gallery item updated');
    } catch (error) {
      console.error('Error updating gallery item:', error);
      toast.error('Failed to update gallery item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (item) => {
    const confirmed = await confirmDialog('Remove this image?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    setSaving(true);

    try {
      const { error } = await supabase
        .from('portal_gallery_items')
        .delete()
        .eq('id', item.id);

      if (error) {
        throw error;
      }

      if (item.storage_path) {
        await supabase.storage.from(IMAGE_BUCKET).remove([item.storage_path]);
      }

      const nextItems = localItems.filter((galleryItem) => galleryItem.id !== item.id);
      setLocalItems(nextItems);
      onItemsUpdate?.(sectionId, nextItems);
      toast.success('Gallery item deleted');
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      toast.error('Failed to delete gallery item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (item) => {
    setSaving(true);
    try {
      const nextVisible = !item.visible;
      const { error } = await supabase
        .from('portal_gallery_items')
        .update({ visible: nextVisible })
        .eq('id', item.id);

      if (error) {
        throw error;
      }

      const nextItems = localItems.map((galleryItem) =>
        galleryItem.id === item.id ? { ...galleryItem, visible: nextVisible } : galleryItem
      );
      setLocalItems(nextItems);
      onItemsUpdate?.(sectionId, nextItems);
      toast.success(`Image ${nextVisible ? 'shown' : 'hidden'} on portal`);
    } catch (error) {
      console.error('Error toggling gallery item visibility:', error);
      toast.error('Failed to update visibility: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (activeId, overId) => {
    if (activeId === overId) return;

    const fromIndex = localItems.findIndex((item) => item.id === activeId);
    const toIndex = localItems.findIndex((item) => item.id === overId);

    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...localItems];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const updates = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    setLocalItems(updates);

    setSaving(true);
    try {
      for (const item of updates) {
        await supabase
          .from('portal_gallery_items')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id);
      }
      onItemsUpdate?.(sectionId, updates);
    } catch (error) {
      console.error('Error reordering gallery items:', error);
      toast.error('Failed to reorder gallery items');
      setLocalItems(localItems);
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over) return;
    handleReorder(active.id, over.id);
  };

  return (
    <div className="space-y-3" aria-busy={saving}>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">Gallery Images</label>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            + Add Image
          </button>
        )}
      </div>

      {isAdding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
          <input
            type="text"
            placeholder="Title (optional)"
            value={newItem.title}
            onChange={(e) => setNewItem((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <textarea
            placeholder="Caption (optional)"
            value={newItem.caption}
            onChange={(e) => setNewItem((prev) => ({ ...prev, caption: e.target.value }))}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="w-full text-sm text-gray-600"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddItem}
              disabled={saving || !newItem.file}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Uploading...' : 'Add Image'}
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewItem({ title: '', caption: '', file: null });
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {localItems.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No images yet. Click "Add Image" to upload one.</p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {localItems.map((item) => (
                <SortableGalleryCard key={item.id} item={item}>
                  {({ attributes, listeners }) => (
                    editingId === item.id ? (
                      <GalleryEditForm
                        item={item}
                        saving={saving}
                        onCancel={() => setEditingId(null)}
                        onSave={(updates) => handleUpdateItem(item.id, updates)}
                      />
                    ) : (
                      <div>
                        <div className="flex gap-3">
                          <button
                            {...attributes}
                            {...listeners}
                            className="px-2 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded self-start"
                            title="Drag to reorder"
                            aria-label="Drag gallery item to reorder"
                            type="button"
                          >
                            ☰
                          </button>
                          <div className="flex-1 flex gap-3">
                            <div className="w-24 h-24 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                              <img
                                src={item.image_url}
                                alt={item.title || 'Gallery image'}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 text-sm">{item.title || 'Untitled image'}</h4>
                              {item.caption && <p className="text-xs text-gray-600 mt-1">{item.caption}</p>}
                              <p className="text-[11px] text-gray-400 mt-2 break-all">{item.image_url}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1 mt-3">
                          <button
                            onClick={() => setEditingId(item.id)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleVisibility(item)}
                            disabled={saving}
                            className={`text-xs px-2 py-1 rounded ${
                              item.visible
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } disabled:opacity-50`}
                          >
                            {item.visible ? '✓ Visible' : '✕ Hidden'}
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </SortableGalleryCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <p className="text-xs text-gray-500">
        Upload engaging photos to showcase your team. Images are displayed in the order shown above.
      </p>
    </div>
  );
}

function SortableGalleryCard({ item, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 ${item.visible ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300'} ${
        isDragging ? 'shadow-lg z-10' : ''
      }`}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function GalleryEditForm({ item, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(item.title || '');
  const [caption, setCaption] = useState(item.caption || '');

  const handleSubmit = () => {
    onSave({
      title: title.trim() || null,
      caption: caption.trim() || null
    });
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        placeholder="Caption (optional)"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        rows={2}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

